import { API_BASE } from '../apiBase';
// src/inventory/Cart.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function Cart() {
  const [cart, setCart] = useState(JSON.parse(localStorage.getItem('cart')) || []);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    axios
      .get(`${API_BASE}/items`, { headers: { Authorization: token } })
      .then((res) => setItems(res.data.items || []))
      .catch(() => navigate('/'));
  }, [navigate]);

  const updateQuantity = (index, quantity) => {
    const newCart = [...cart];
    newCart[index].quantity = Math.max(1, parseInt(quantity));
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const toggleAction = (index) => {
    const newCart = [...cart];
    newCart[index].action = newCart[index].action === 'withdraw' ? 'deposit' : 'withdraw';
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const removeItem = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const confirmTransaction = async () => {
    const token = localStorage.getItem('token');
    await axios.post(
      `${API_BASE}/confirm`,
      { cart },
      { headers: { Authorization: token } }
    );
    setCart([]);
    localStorage.removeItem('cart');
    navigate('/inventory');
  };

  // Calculate totals
  const totalWithdrawals = cart
    .filter((item) => item.action === 'withdraw')
    .reduce((sum, item) => sum + item.quantity * item.price, 0);

  const totalDeposits = cart
    .filter((item) => item.action === 'deposit')
    .reduce((sum, item) => sum + item.quantity * item.price, 0);

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <h1 className="text-3xl font-bold tracking-tight mb-8">Cart</h1>

        {cart.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-8">No items added yet.</p>
            <button
              onClick={() => navigate('/inventory')}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              ← Return to Inventory
            </button>
          </div>
        ) : (
          <>
            {/* ===== MOBILE: stacked cards (no sideways scrolling) ===== */}
            <div className="sm:hidden space-y-3 mb-8">
              {cart.map((item, index) => {
                const availableQty =
                  items.find((i) => i._id === item.itemId)?.quantity || 0;
                return (
                  <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="font-semibold text-gray-900 leading-snug">{item.name}</p>
                      <button
                        onClick={() => toggleAction(index)}
                        className={`shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md capitalize ${
                          item.action === 'withdraw'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {item.action}
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 uppercase font-medium">Qty</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(index, e.target.value)}
                          min="1"
                          max={item.action === 'withdraw' ? availableQty : undefined}
                          className="w-16 px-2 py-2 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <p className="text-sm text-gray-500">
                        ${item.price.toFixed(2)} × {item.quantity} ={' '}
                        <span className="font-bold text-gray-900">${(item.quantity * item.price).toFixed(2)}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(index)}
                      className="mt-3 w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
                    >
                      Remove · Return to {item.location}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* ===== DESKTOP: table ===== */}
            <div className="hidden sm:block bg-white rounded-xl shadow-sm overflow-x-auto border border-gray-200 mb-8">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cart.map((item, index) => {
                    const availableQty =
                      items.find((i) => i._id === item.itemId)?.quantity || 0;
                    return (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleAction(index)}
                            className={`px-4 py-2 text-sm font-medium rounded-md ${
                              item.action === 'withdraw'
                                ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                            }`}
                          >
                            {item.action}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(index, e.target.value)}
                            min="1"
                            max={item.action === 'withdraw' ? availableQty : undefined}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${item.price.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ${(item.quantity * item.price).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => removeItem(index)}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md text-sm transition-colors"
                          >
                            Return to {item.location}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals & Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Total Value (Withdrawals)</p>
                  <p className="text-2xl font-bold text-red-900">${totalWithdrawals.toFixed(2)}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-green-800">Total Value (Deposits)</p>
                  <p className="text-2xl font-bold text-green-900">${totalDeposits.toFixed(2)}</p>
                </div>
              </div>

              {/* Stacked full-width on phones; row on desktop */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/inventory')}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  ← Return to Inventory
                </button>
                <button
                  onClick={() => {
                    if (!cart.length) return;
                    if (!window.confirm('Clear all transactions from the cart?')) return;
                    setCart([]);
                    localStorage.removeItem('cart');
                  }}
                  disabled={!cart.length}
                  className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium text-white transition-colors ${
                    cart.length
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Clear All Transactions
                </button>
                <button
                  onClick={confirmTransaction}
                  disabled={!cart.length}
                  className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium text-white transition-colors ${
                    cart.length
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Confirm Transaction
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Cart;